import RailsUtility from '../utilities/rails';
import { hFetch, publishErrorUnlessSuccess, type HError } from './hFetch';

type ListJobsParams = {
  status: string;
  sort: string;
  order: string;
};

type ListJobsResults = {
  records: V2Job[];
  total_active: number;
  total_inactive: number;
};

export const listJobs = async (queryParams: ListJobsParams) => {
  // Convert status to API format if present
  const apiParams = {
    ...queryParams,
    status: queryParams.status ? RailsUtility.convertStatusToApi(queryParams.status) : queryParams.status,
  };

  const data = await hFetch<ListJobsResults>({ url: `/api/v2_jobs?${new URLSearchParams(apiParams).toString()}` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get Jobs' });

  // Convert status back to frontend format in the response
  if (data && data.records) {
    data.records = data.records.map((record: V2Job) => ({
      ...record,
      status: RailsUtility.convertStatusFromApi(record.status),
    }));
  }

  return data;
};

export const getJob = async ({ id }: { id: string }) => {
  const data = await hFetch({ url: `/api/v2_jobs/${id}` });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Get Job' });
  return data;
};

export const updateJob = async (
  { id }: { id: number },
  { updatedJobAttributes }: { updatedJobAttributes: JobAttributes }
) => {
  const data = await hFetch({ url: `/api/v2_jobs/${id}`, method: 'PATCH', payload: updatedJobAttributes });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Update Job' });
  return data;
};

export const createJob = async (newJobAttributes: V2Job) => {
  const data = await hFetch({ url: '/api/v2_jobs', method: 'POST', payload: newJobAttributes });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Create Job' });
  return data;
};

export const createJobLocation = async (
  { id }: { id: number },
  { newJobLocationAttributes }: { newJobLocationAttributes: JobLocation }
) => {
  const data = await hFetch({
    url: `/api/v2_jobs/${id}/job_locations`,
    method: 'POST',
    payload: newJobLocationAttributes,
  });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Create Job Location' });
  return data;
};

export const deactivateJobLocation = async ({ jobId, locationId }: { jobId: string; locationId: string }) => {
  const data = await hFetch({
    url: `/api/v2_jobs/${jobId}/job_locations/${locationId}`,
    method: 'PATCH',
    payload: { status: 'inactive' },
  });
  publishErrorUnlessSuccess({ data: data as unknown as HError, alertTitle: 'Failed to Delete Job Location' });
  return data;
};

type JobAttributes = {
  status: string;
  resume_required: boolean;
  title: string;
  job_details: JobDetail;
};

type V2Job = {
  id: number;
  team_id: number;
  job_type: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  indeed_ref_id: number;
  qualifying_filter: boolean;
  resume_required: boolean;
  'is_stale?': boolean;
  url: string;
  job_details: JobDetail[];
  job_locations: JobLocation[];
};

type JobDetail = {
  id: number;
  description: string;
  status: string;
  created_at: string;
};

type JobLocation = {
  id: number;
  city: string;
  state: string;
  country: string;
  latitude: string;
  longitude: string;
  status: string;
};
